import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-brand-default disabled:pointer-events-none disabled:opacity-50 cursor-pointer',
  {
    variants: {
      variant: {
        default:
          'sheen sheen-host bg-gradient-primary text-text-inverse shadow hover:bg-gradient-primary-hover',
        destructive: 'sheen sheen-host bg-error text-text-inverse shadow-sm hover:bg-error/80',
        outline:
          'border border-border-base bg-transparent shadow-sm hover:bg-fill-lighter/30 hover:text-text-primary hover:border-text-secondary/50',
        secondary:
          'bg-fill-lighter text-text-primary shadow-sm hover:bg-fill-lighter/80 hover:text-text-primary/80',
        ghost: 'text-text-regular hover:bg-fill-default hover:text-text-primary',
        glass:
          'bg-fill-lighter/30 backdrop-blur-md shadow-inner shadow-border-base/30 hover:bg-fill-lighter/50',
        highlight:
          "sheen sheen-host bg-fill-default text-text-regular font-normal brightness-100 transition-[background-color,opacity,color,filter] duration-150 ease-in-out [@media(hover:hover)]:hover:brightness-[0.8] active:brightness-[0.65] disabled:brightness-100 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        highlightSubtle:
          "sheen sheen-host sheen-soft bg-fill-lighter/90 text-text-secondary font-normal transition-[background-color,opacity,color,filter] duration-150 ease-in-out [@media(hover:hover)]:hover:bg-fill-default/85 [@media(hover:hover)]:hover:text-text-primary active:bg-fill-default/95 disabled:bg-fill-lighter/90 disabled:text-text-secondary [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-3.5",
        plain:
          'bg-transparent text-brand-default shadow-none hover:bg-brand-default/10 border border-brand-default',
        link: 'text-brand-default underline-offset-4 hover:text-brand-hover',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
